import { useState, useCallback } from 'react';
import { Alert, Platform, Linking } from 'react-native';
import * as Contacts from 'expo-contacts';
import * as DocumentPicker from 'expo-document-picker';
import { parsePhoneNumber } from 'libphonenumber-js';
import { ContactData, ContactSource } from '../types/messageForm.types';
import { usePhoneValidation } from './usePhoneValidation';
import { apiService } from '../../../services/api';
import { parseInternationalPhoneNumber } from '../../../utils/phoneNumber';
import { usePermissions } from '../../../hooks/usePermissions';
import { defaultPhoneCountry } from '../../../utils/businessDefaults';

/**
 * Custom hook for contact selection and management
 */
export const useContactSelection = () => {
  const [contacts, setContacts] = useState<ContactData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { normalizePhoneNumber, detectCountry, extractPhoneData } = usePhoneValidation();
  const { hasPermissionAtLeast } = usePermissions();
  const canImportContacts = hasPermissionAtLeast('contacts.import', 'edit');

  /**
   * Request contacts permission
   */
  const requestContactsPermission = useCallback(async (): Promise<boolean> => {
    try {
      const { status: existingStatus } = await Contacts.getPermissionsAsync();
      
      if (existingStatus === 'granted') {
        return true;
      }
      
      const { status } = await Contacts.requestPermissionsAsync();
      
      if (status === 'granted') {
        return true;
      }
      
      Alert.alert(
        'Permission Required',
        'DISLIX needs access to your contacts to import them. Please grant permission in your device settings.',
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Open Settings', 
            onPress: () => {
              if (Platform.OS === 'ios') {
                Linking.openURL('app-settings:');
              } else {
                Linking.openSettings();
              }
            }
          }
        ]
      );
      return false;
    } catch (error) {
      console.error('Error requesting contacts permission:', error);
      Alert.alert('Permission Error', 'Failed to request contacts permission.');
      return false;
    }
  }, []);

  /**
   * Check if contact exists in current business
   */
  const checkContactExists = useCallback(async (phoneNumber: string): Promise<{
    exists: boolean;
    contact?: any;
  }> => {
    try {
      const response = await apiService.checkContactExists(phoneNumber);
      if (response.statusCode === 1000 && response.data?.exists) {
        return {
          exists: true,
          contact: response.data.contact
        };
      }
      return { exists: false };
    } catch (error) {
      console.error('Error checking contact existence:', error);
      return { exists: false };
    }
  }, []);

  /**
   * Normalize phone number from various formats
   */
  const normalizePhoneFromString = useCallback((phone: string, countryCode: string = defaultPhoneCountry()): string => {
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // If starts with +, use as is
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // Try to detect country from phone
    const detected = detectCountry(phone);
    return normalizePhoneNumber(cleaned, detected || countryCode);
  }, [detectCountry, normalizePhoneNumber]);

  /**
   * Add a single contact
   */
  const addContact = useCallback(async (
    phoneNumber: string,
    phoneData: any,
    name?: string
  ): Promise<ContactData | null> => {
    try {
      const { normalized, dialCode, countryCode } = extractPhoneData(phoneData, phoneNumber);

      // extractPhoneData returns '' for a still-masked value rather than
      // manufacturing a garbage number from its visible digits.
      if (!normalized) {
        Alert.alert('Error', 'This phone number is masked — please enter the real number.');
        return null;
      }

      // Check if contact exists
      const existsCheck = await checkContactExists(normalized);
      
      const contact: ContactData = {
        name: name || normalized,
        phoneNumber: normalized,
        countryCode: countryCode,
        dialCode: dialCode,
        isValid: true,
        isExisting: existsCheck.exists,
        existingContactId: existsCheck.contact?._id,
      };

      if (existsCheck.exists && existsCheck.contact) {
        contact.name = existsCheck.contact.name || contact.name;
      }

      setContacts(prev => [...prev, contact]);
      return contact;
    } catch (error) {
      console.error('Error adding contact:', error);
      Alert.alert('Error', 'Failed to add contact');
      return null;
    }
  }, [checkContactExists, extractPhoneData]);

  /**
   * Remove a contact by index
   */
  const removeContact = useCallback((index: number) => {
    setContacts(prev => prev.filter((_, i) => i !== index));
  }, []);

  /**
   * Update a contact
   */
  const updateContact = useCallback((index: number, updates: Partial<ContactData>) => {
    setContacts(prev => prev.map((contact, i) => 
      i === index ? { ...contact, ...updates } : contact
    ));
  }, []);

  /**
   * Import contacts from device
   */
  const importFromDevice = useCallback(async (): Promise<ContactData[]> => {
    if (!canImportContacts) return [];
    try {
      const hasPermission = await requestContactsPermission();
      if (!hasPermission) {
        return [];
      }

      setIsLoading(true);

      const { data } = await Contacts.getContactsAsync({
        fields: [
          Contacts.Fields.Name,
          Contacts.Fields.PhoneNumbers,
        ],
      });

      if (data.length === 0) {
        Alert.alert('No Contacts', 'No contacts found on your device.');
        return [];
      }

      const importedContacts: ContactData[] = [];

      for (const contact of data) {
        if (!contact.phoneNumbers || contact.phoneNumbers.length === 0) {
          continue;
        }

        const phoneNumber = contact.phoneNumbers[0].number || '';
        if (!phoneNumber) continue;

        // Use centralized phone number parsing (same as WhatsAppContactModal)
        const parsed = parseInternationalPhoneNumber(phoneNumber);
        
        let normalized: string;
        let detectedCountry: string;
        let dialCode: string;
        
        if (parsed && parsed.isValid) {
          normalized = parsed.e164Format;
          detectedCountry = parsed.country || defaultPhoneCountry();
          dialCode = parsed.countryCode;
        } else {
          // Fallback to old logic if parsing fails
          normalized = normalizePhoneFromString(phoneNumber);
          detectedCountry = detectCountry(normalized);
          dialCode = '+20'; // Default
          try {
            const parsedFallback = parsePhoneNumber(normalized);
            if (parsedFallback) {
              dialCode = `+${parsedFallback.countryCallingCode}`;
            }
          } catch (e) {
            // Keep default
          }
        }

        const existsCheck = await checkContactExists(normalized);

        importedContacts.push({
          name: contact.name || normalized,
          phoneNumber: normalized,
          countryCode: detectedCountry,
          dialCode: dialCode,
          isValid: true,
          isExisting: existsCheck.exists,
          existingContactId: existsCheck.contact?._id,
        });
      }

      setContacts(prev => [...prev, ...importedContacts]);
      return importedContacts;
    } catch (error: any) {
      console.error('Error importing from device:', error);
      Alert.alert('Error', error.message || 'Failed to import contacts from device');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [canImportContacts, requestContactsPermission, normalizePhoneFromString, checkContactExists, detectCountry, normalizePhoneNumber]);

  /**
   * Import contacts from CSV/Excel file
   */
  const importFromFile = useCallback(async (): Promise<ContactData[]> => {
    if (!canImportContacts) return [];
    try {
      setIsLoading(true);

      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'text/csv',
          'text/comma-separated-values',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return [];
      }

      const file = result.assets[0];
      if (!file) {
        Alert.alert('Error', 'No file selected');
        return [];
      }

      // Upload file to backend for parsing
      const response = await apiService.uploadBulkContacts(
        file.uri,
        file.name,
        file.mimeType || 'text/csv',
        'whatsapp'
      );

      if (response.statusCode === 1000 && response.data) {
        const successful = response.data.successful || response.data.contacts || [];
        
        // Check for merged/updated contacts (duplicates that were merged with existing)
        const mergedContacts = successful.filter((c: any) => !c.isNew);
        if (mergedContacts.length > 0) {
          const mergedNames = mergedContacts.map((c: any) => c.profile?.name || c.phoneNumber).join(', ');
          Alert.alert(
            'Existing Contacts Merged',
            `${mergedContacts.length} contact(s) already existed and were updated with new information: ${mergedNames}`,
            [{ text: 'OK' }]
          );
        }
        
        const importedContacts: ContactData[] = successful.map((contact: any) => {
          const safeContact = contact || {};
          const displayName = String(
            safeContact.name ||
            safeContact.profile?.name ||
            safeContact.fullName ||
            safeContact.contactName ||
            ''
          ).trim();
          const rawPhone = String(safeContact.phoneNumber || safeContact.phone_number || safeContact.primaryIdentifier || '');
          if (!rawPhone) {
            return {
              name: displayName || 'Unknown',
              phoneNumber: '',
              countryCode: defaultPhoneCountry(),
              dialCode: '+20',
              isValid: false,
              isExisting: safeContact.isExisting || false,
              errors: ['Missing phone number']
            };
          }

          // Ensure phone number has + prefix for proper parsing (backend CSV returns without +)
          let phoneToParse = rawPhone.trim();
          if (!phoneToParse.startsWith('+')) {
            phoneToParse = `+${phoneToParse}`;
          }
          
          // Use centralized phone number parsing (same as WhatsAppContactModal)
          const parsed = parseInternationalPhoneNumber(phoneToParse);
          
          // Accept parsed result even if format validation is strict
          // as long as we got country information (handles cases like 9661023356631)
          if (parsed && parsed.country) {
            return {
              name: displayName || rawPhone,
              phoneNumber: parsed.e164Format, // Use E.164 format for consistency
              countryCode: parsed.country, // e.g., 'SA', 'EG'
              dialCode: parsed.countryCode, // e.g., '+966', '+20'
              isValid: parsed.isValid, // Keep validation flag but still use the parsed data
              isExisting: safeContact.isExisting || false,
            };
          }
          
          // Fallback if parsing fails - try to detect country manually
          console.warn('📞 [useContactSelection] Failed to parse phone number:', rawPhone);
          let fallbackCountry = defaultPhoneCountry();
          let fallbackDialCode = '+20';
          
          // Try to detect from first digits
          const digitsOnly = rawPhone.replace(/\D/g, '');
          if (digitsOnly.startsWith('966')) {
            fallbackCountry = 'SA';
            fallbackDialCode = '+966';
          } else if (digitsOnly.startsWith('20')) {
            fallbackCountry = defaultPhoneCountry();
            fallbackDialCode = '+20';
          } else if (digitsOnly.startsWith('971')) {
            fallbackCountry = 'AE';
            fallbackDialCode = '+971';
          } else if (digitsOnly.startsWith('1') && digitsOnly.length > 5) {
            fallbackCountry = 'US';
            fallbackDialCode = '+1';
          }
          
          return {
            name: displayName || rawPhone,
            phoneNumber: phoneToParse, // Keep with + prefix
            countryCode: fallbackCountry,
            dialCode: fallbackDialCode,
            isValid: false,
            isExisting: safeContact.isExisting || false,
            errors: ['Invalid phone number format']
          };
        });

        if (importedContacts.length === 0) {
          const failed = response.data.failed || [];
          const failMsg = failed[0]?.errors?.[0] || failed[0]?.error || 'No valid contacts found in the file.';
          Alert.alert('No Contacts Imported', failMsg, [{ text: 'OK' }]);
          return [];
        }

        setContacts(prev => [...prev, ...importedContacts]);
        return importedContacts;
      } else {
        throw new Error(response.errors?.[0]?.message || 'Failed to import contacts');
      }
    } catch (error: any) {
      console.error('Error importing from file:', error);
      Alert.alert('Error', error.message || 'Failed to import contacts from file');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [canImportContacts, detectCountry]);

  /**
   * Validate all contacts
   */
  const validateContacts = useCallback((contactsToValidate: ContactData[]): ContactData[] => {
    return contactsToValidate.map(contact => {
      const errors: string[] = [];

      if (!contact.phoneNumber || contact.phoneNumber.length < 8) {
        errors.push('Invalid phone number');
      }

      if (!contact.name || contact.name.trim().length < 2) {
        errors.push('Contact name is too short');
      }

      return {
        ...contact,
        isValid: errors.length === 0,
        errors: errors.length > 0 ? errors : undefined
      };
    });
  }, []);

  /**
   * Clear all contacts
   */
  const clearContacts = useCallback(() => {
    setContacts([]);
  }, []);

  /**
   * Check for duplicates
   */
  const findDuplicates = useCallback((newContact: ContactData): number[] => {
    return contacts
      .map((contact, index) => contact.phoneNumber === newContact.phoneNumber ? index : -1)
      .filter(index => index !== -1);
  }, [contacts]);

  return {
    contacts,
    isLoading,
    addContact,
    removeContact,
    updateContact,
    importFromDevice,
    importFromFile,
    validateContacts,
    clearContacts,
    findDuplicates,
    checkContactExists
  };
};

